import { asLimitRefusal, type LimitRefusal } from '@/lib/api/billing'
import { ApiError } from '@/lib/api/errors'

/**
 * What a drop of files is allowed to be, and what happens when the gateway
 * says no — as pure functions, so the rules can be checked without a click.
 *
 * The numbers here are the gateway's, read off the handler rather than off
 * the artboard: `isValidFileType` in `api/internal/api/upload_handlers.go`
 * accepts **.pdf, .png, .jpg, .jpeg and nothing else** (the artboard's list
 * names tiff, which the handler refuses with "Filetype not supported"), and
 * the body is capped by `limits.upload_max_size_mb`, whose default is **50**
 * (the artboard says 32, which is the Flutter client's own constant and was
 * never the server's).
 */

/** The extensions `isValidFileType` accepts, lower-cased, without the dot. */
export const UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'] as const

/** For the file input's `accept`. Same list, so the picker cannot offer a
 *  file the handler will refuse. */
export const UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.pdf'

export const UPLOAD_FORMATS_LABEL = 'Unterstützte Formate: jpg, png, pdf'

/**
 * The gateway's default body cap, in MB.
 *
 * **Gap:** no endpoint reports `limits.upload_max_size_mb` to a signed-in
 * client — only `GET /public/upload/:token` carries it (`maxSizeMb`), and
 * that needs an upload-link token. So this mirrors the server default, the
 * dialog takes an override, and a 413 is reported as "zu groß" whatever this
 * number says. The check here only saves a doomed upload; it is not the
 * enforcement.
 */
export const DEFAULT_MAX_UPLOAD_MB = 50

export type UploadItemStatus =
  /** Waiting its turn — uploads are strictly one at a time. */
  | 'queued'
  | 'uploading'
  | 'done'
  /** The request failed and may be retried on its own. */
  | 'failed'
  /** Refused before sending: too large / wrong format. */
  | 'tooLarge'
  | 'unsupported'
  /** Never sent because an account-wide refusal stopped the queue. Kept in
   *  the list on purpose: a blocked queue that silently vanished would be a
   *  person losing twenty files to one 402. */
  | 'blocked'

export interface UploadItem {
  id: string
  file: File
  name: string
  sizeBytes: number
  isPdf: boolean
  status: UploadItemStatus
  /** 0..1, only meaningful while `uploading`. */
  progress: number
  /** German, shown on the row. */
  error?: string
  /** The id the gateway assigned. For a PDF this id does **not** survive:
   *  the converter replaces the placeholder document with one document per
   *  rendered page and deletes it (`convertDocument` in
   *  `api/internal/converter/worker.go`). */
  documentId?: string
}

let counter = 0

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

export function isPdfName(name: string): boolean {
  return extensionOf(name) === 'pdf'
}

/** A file's verdict before a byte is sent. */
export function checkFile(file: File, maxBytes: number): 'ok' | 'tooLarge' | 'unsupported' {
  if (!(UPLOAD_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return 'unsupported'
  }
  if (maxBytes > 0 && file.size > maxBytes) return 'tooLarge'
  return 'ok'
}

export function makeItem(file: File, maxSizeMb = DEFAULT_MAX_UPLOAD_MB): UploadItem {
  const verdict = checkFile(file, maxSizeMb * 1024 * 1024)
  counter += 1
  return {
    id: `${Date.now()}-${counter}`,
    file,
    name: file.name,
    sizeBytes: file.size,
    isPdf: isPdfName(file.name),
    status: verdict === 'ok' ? 'queued' : verdict,
    progress: 0,
    error:
      verdict === 'tooLarge'
        ? `zu groß · max. ${maxSizeMb} MB`
        : verdict === 'unsupported'
          ? 'Format nicht unterstützt · jpg, png oder pdf'
          : undefined,
  }
}

export interface QueueSummary {
  total: number
  done: number
  failed: number
  /** Queued, uploading or blocked — nothing has been stored for these. */
  pending: number
  uploading: number
  /** Rejected before sending. */
  rejected: number
  blocked: number
  /** Ids of everything that landed. */
  documentIds: string[]
  /**
   * Ids that can be analysed straight away — images only. A PDF's id is
   * gone as soon as the converter has rendered it, so queueing an analysis
   * against it would be a job on a document that no longer exists.
   */
  analysableIds: string[]
  pdfCount: number
}

export function queueSummary(items: UploadItem[]): QueueSummary {
  const done = items.filter((item) => item.status === 'done')
  return {
    total: items.length,
    done: done.length,
    failed: items.filter((item) => item.status === 'failed').length,
    pending: items.filter(
      (item) => item.status === 'queued' || item.status === 'uploading' || item.status === 'blocked',
    ).length,
    uploading: items.filter((item) => item.status === 'uploading').length,
    rejected: items.filter((item) => item.status === 'tooLarge' || item.status === 'unsupported')
      .length,
    blocked: items.filter((item) => item.status === 'blocked').length,
    documentIds: done.map((item) => item.documentId).filter((id): id is string => Boolean(id)),
    analysableIds: done
      .filter((item) => !item.isPdf)
      .map((item) => item.documentId)
      .filter((id): id is string => Boolean(id)),
    pdfCount: items.filter((item) => item.isPdf).length,
  }
}

/** True while a file is in flight or waiting for one — closing now loses it. */
export function queueIsLive(items: UploadItem[]): boolean {
  return items.some((item) => item.status === 'queued' || item.status === 'uploading')
}

export type UploadRefusal =
  /** 402 `page_limit_reached`: the plan's page quota. */
  | { kind: 'pageLimit'; refusal: LimitRefusal }
  /** 402 `no_credits`: the balance will not cover the next page. Only ever
   *  reached where the deployment turned credit enforcement on. */
  | { kind: 'credits'; balance: number; required: number }
  /** 403 `email_unverified`: the grace window has run out. */
  | { kind: 'unverified' }
  /** 413 — the body cap, enforced by `http.MaxBytesReader`. */
  | { kind: 'tooLarge' }
  /** 503 — past the per-instance upload cap. Retryable, and the reason this
   *  dialog never uploads two files at once. */
  | { kind: 'busy' }
  | { kind: 'other'; message: string }

export function classifyUploadError(error: unknown): UploadRefusal {
  const api = ApiError.from(error)

  const limit = asLimitRefusal(api)
  if (limit?.code === 'page_limit_reached') return { kind: 'pageLimit', refusal: limit }

  if (api.status === 402 && api.code === 'no_credits') {
    const details = api.details ?? {}
    return {
      kind: 'credits',
      balance: Number(details.balance ?? 0),
      required: Number(details.required ?? 0),
    }
  }
  if (api.status === 403 && api.code === 'email_unverified') return { kind: 'unverified' }
  if (api.status === 413) return { kind: 'tooLarge' }
  if (api.status === 503) return { kind: 'busy' }
  return { kind: 'other', message: api.message || 'Hochladen fehlgeschlagen' }
}

/**
 * Whether a refusal is about the account rather than the file — in which
 * case the rest of the queue must not be sent. Firing nineteen more requests
 * that are all going to be refused wastes the person's time and the
 * gateway's, and buries the one message that matters.
 */
export function stopsQueue(refusal: UploadRefusal): boolean {
  return refusal.kind === 'pageLimit' || refusal.kind === 'credits' || refusal.kind === 'unverified'
}

/** The short reason shown on the file's own row. */
export function rowMessage(refusal: UploadRefusal): string {
  switch (refusal.kind) {
    case 'pageLimit':
      return 'Kontingent aufgebraucht'
    case 'credits':
      return 'Guthaben reicht nicht'
    case 'unverified':
      return 'E-Mail-Adresse nicht bestätigt'
    case 'tooLarge':
      return 'zu groß'
    case 'busy':
      return 'Server ausgelastet — bitte erneut versuchen'
    default:
      return refusal.message
  }
}

/**
 * The block above the file list, when the queue was stopped. Each refusal
 * gets its own words because each needs a different action from the reader —
 * that is the whole reason they are told apart.
 */
export function refusalNotice(refusal: UploadRefusal): {
  title: string
  body: string
  tone: 'warning' | 'danger'
} {
  switch (refusal.kind) {
    case 'pageLimit':
      return {
        tone: 'warning',
        title: 'Ihr Seitenkontingent ist aufgebraucht',
        body: `${refusal.refusal.used} von ${refusal.refusal.limit} Seiten im Tarif „${refusal.refusal.plan}“ verbraucht. Alles bereits Hochgeladene bleibt vollständig nutzbar — lesen, prüfen und exportieren geht weiter. Für neue Seiten löschen Sie ältere oder wechseln den Tarif.`,
      }
    case 'credits':
      return {
        tone: 'warning',
        title: 'Ihr Guthaben reicht für diesen Upload nicht',
        body: `Sie haben ${refusal.balance} Guthaben, benötigt werden ${refusal.required} für die nächste Seite. Lesen, Prüfen und Exportieren bleiben jederzeit möglich.`,
      }
    case 'unverified':
      return {
        tone: 'warning',
        title: 'Uploads pausiert, bis Ihre E-Mail-Adresse bestätigt ist',
        body: 'Bitte bestätigen Sie den Link in unserer E-Mail. Danach können Sie hier weitermachen — die wartenden Dateien bleiben in der Liste.',
      }
    default:
      return {
        tone: 'danger',
        title: 'Hochladen fehlgeschlagen',
        body: rowMessage(refusal),
      }
  }
}

/** "12,4 MB" — German decimal comma, one place. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}
